import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    PurchaseOrder,
    UpgradeItemType,
    UpgradeLabourType,
    UpgradeStoreTypeSetting,
    UpliftRequest
} from '@app/models/closedown-detail.model';
import { catchError, defer, Observable, of, switchMap } from 'rxjs';
import { Preferences } from '@capacitor/preferences';
import { StorageKey } from '@app/shared/enums';
import { environment } from 'src/environments/environment';
import { BaseService } from '../base/base.service';
import { JobWithPurchaseOrdersDto } from '@app/models/job-with-purchase-orders.model';
import { SubmittedInvoiceFilterDto, SubmittedInvoicePageDto } from '@app/models/submitted-invoice.model';

@Injectable({
    providedIn: 'root'
})
export class InvoiceService extends BaseService {
    readonly BASE_URL = `${environment.gatewayAPI_V1}/Invoices/`;
    //readonly BASE_URL = "https://localhost:7100/api/v1/";

    constructor(private httpClient: HttpClient) {
        super();
    }

    public getUpliftPurchaseOrder(jobId: string, companyId: string): Observable<PurchaseOrder | null> {
        const url = `${this.BASE_URL}PurchaseOrder/GetUpliftPOByJobIdAndCompanyId`;
        const params = new HttpParams()
            .set('jobId', jobId)
            .set('companyId', companyId);

        return this.httpClient.get<PurchaseOrder | null>(url, { params }).pipe(
            catchError((error: HttpErrorResponse) => error.status === 404
                ? of(null)
                : this.handleError(error))
        );
    }

    public getUpgradeItemTypes(): Observable<UpgradeItemType[]> {
        const url = `${this.BASE_URL}PurchaseOrder/GetUpgradeItemTypes`;

        return this.httpClient.get<UpgradeItemType[]>(url).pipe(
            catchError(this.handleError)
        );
    }

    public getUpgradeLabourTypes(): Observable<UpgradeLabourType[]> {
        const url = `${this.BASE_URL}PurchaseOrder/GetUpgradeLabourTypes`;

        return this.httpClient.get<UpgradeLabourType[]>(url).pipe(
            catchError(this.handleError)
        );
    }

    public getUpgradeStoreTypeSettings(): Observable<UpgradeStoreTypeSetting[]> {
        const url = `${this.BASE_URL}/Invoices/getStoreTypeSettings`;

        return this.httpClient.get<UpgradeStoreTypeSetting[]>(url).pipe(
            catchError(this.handleError)
        );
    }

    public getUpgradeTaxRate(storeId: number): Observable<number> {
        const url = `${this.BASE_URL}/Invoices/GetInvoiceLineItemTaxRate`;
        const params = new HttpParams().set('storeid', storeId);

        return this.httpClient.get<number>(url, { params }).pipe(
            catchError(this.handleError)
        );
    }

    public createUpliftRequest(request: UpliftRequest): Observable<string> {
        const url = `${this.BASE_URL}PurchaseOrder/UpliftRequest`;
        //const url = `https://localhost:7100/api/v1/PurchaseOrder/UpliftRequest`;
        

        return this.httpClient.post<string>(url, request).pipe(
            catchError(this.handleError)
        );
    }

    public getJobsWithApprovedReceiptPurchaseOrders(): Observable<JobWithPurchaseOrdersDto[]> {
        const url = `${this.BASE_URL}Job/GetJobsWithApprovedReceiptPurchaseOrders`;
        //const url = `https://localhost:7100/api/v1/Job/GetJobsWithApprovedReceiptPurchaseOrders`;
        

        return defer(() => Preferences.get({ key: StorageKey[StorageKey.CURRENTPROFILE] })).pipe(
            switchMap(profile => {
                const params = profile.value
                    ? new HttpParams().set('CompanyId', profile.value) : new HttpParams();
                return this.httpClient.get<JobWithPurchaseOrdersDto[]>(url, { params });
            }),
            catchError(this.handleError)
        );
    }

    public getJobWithApprovedReceipt(jobId: string): Observable<JobWithPurchaseOrdersDto> {
        const url = `${this.BASE_URL}Job/GetJobWithApprovedReceipt`;
        const params = new HttpParams().set('jobId', jobId);

        return this.httpClient.get<JobWithPurchaseOrdersDto>(url, { params }).pipe(
            catchError(this.handleError)
        );
    }

    public getSubmittedInvoicesByFilter(filter: SubmittedInvoiceFilterDto): Observable<SubmittedInvoicePageDto> {
        const url = `${this.BASE_URL}PurchaseInvoice/GetSubmittedInvoicesByFilter`;
        return defer(() => Preferences.get({ key: StorageKey[StorageKey.CURRENTPROFILE] })).pipe(
            switchMap(profile => {
                const query: SubmittedInvoiceFilterDto = {
                    ...filter,
                    companyId: profile.value || null,
                    pageNumber: filter.pageNumber ?? 1,
                    pageSize: filter.pageSize ?? 50
                };
                let params = new HttpParams();
                Object.entries(query).forEach(([key, value]) => {
                    if (value !== null && value !== undefined) {
                        params = params.set(key, value);
                    }
                });
                return this.httpClient.get<SubmittedInvoicePageDto>(url, { params });
            }),
            catchError(this.handleError)
        );
    }

}